/**
 * colors.js — Semantic color mappings for the MUI theme.
 *
 * All values come from palette.js — do NOT hardcode hex strings here.
 * To change any color globally, edit palette.js only.
 *
 * Shape must stay stable: component overrides destructure keys like
 * { primary, error, gradients, badgeColors, ... } from this file.
 */

import palette from "assets/theme/base/palette";

export default {
  // ─── Surfaces ─────────────────────────────────────────────────────────────
  background: {
    default: palette.brand[50],
  },

  authBackground: {
    default: palette.navy[800],
    gradient: `linear-gradient(135deg, ${palette.brand[400]} 0%, ${palette.brand[600]} 25%, ${palette.brand[900]} 100%)`,
  },

  // ─── Text ─────────────────────────────────────────────────────────────────
  text: {
    main:  palette.neutral[500],
    focus: palette.neutral[500],
  },

  // ─── Utility ──────────────────────────────────────────────────────────────
  transparent: {
    main: palette.transparent,
  },

  white: {
    main:  palette.white,
    focus: palette.white,
  },

  black: {
    light: palette.black,
    main:  palette.black,
    focus: palette.black,
  },

  // ─── Semantic roles ───────────────────────────────────────────────────────
  primary: {
    main:  palette.brand[500],
    focus: palette.brand[400],
    light: palette.brand[100],
    dark:  palette.brand[700],
  },

  secondary: {
    main:  palette.neutral[500],
    focus: palette.neutral[400],
  },

  info: {
    main:  palette.brand[500],
    focus: palette.brand[700],
    light: palette.brand[100],
  },

  // Maps to orange accent (legacy "success" mapping kept for backward compat)
  success: {
    main:  palette.orange[500],
    focus: palette.orange[500],
    light: palette.orange[100],
  },

  green: {
    main:  palette.green[500],
    focus: palette.green[400],
    light: palette.green[100],
    dark:  palette.green[700],
  },

  warning: {
    main:  palette.amber[600],
    focus: palette.amber[500],
    light: palette.amber[100],
  },

  error: {
    main:  palette.red[500],
    focus: palette.red[400],
    light: palette.red[100],
    dark:  palette.red[700],
  },

  light: {
    main:  palette.neutral[100],
    focus: palette.neutral[100],
  },

  dark: {
    main:  palette.navy[800],
    focus: palette.navy[900],
    light: palette.navy[600],
  },

  greyMedium: {
    main:  palette.neutral[400],
    focus: palette.neutral[400],
  },

  // Full neutral scale — used for borders, backgrounds, dividers
  grey: {
    100: palette.neutral[50],
    200: palette.neutral[100],
    300: palette.neutral[200],
    400: palette.neutral[300],
    500: palette.neutral[400],
    600: palette.neutral[600],
    700: palette.neutral[700],
    800: palette.neutral[800],
    900: palette.neutral[900],
  },

  // ─── Gradients ────────────────────────────────────────────────────────────
  gradients: {
    primary: {
      main:  palette.brand[500],
      state: palette.brand[600],
    },

    secondary: {
      main:  palette.neutral[600],
      state: palette.neutral[700],
    },

    info: {
      main:  palette.brand[300],
      state: palette.brand[500],
    },

    success: {
      main:  palette.orange[400],
      state: palette.orange[500],
    },

    warning: {
      main:  palette.amber[400],
      state: palette.amber[600],
    },

    error: {
      main:  palette.red[400],
      state: palette.red[600],
    },

    light: {
      main:  palette.neutral[50],
      state: palette.neutral[200],
    },

    dark: {
      main:  palette.navy[700],
      state: palette.navy[900],
    },
  },

  // ─── Social media (brand-owned; kept from palette.white/black only) ───────
  socialMediaColors: {
    facebook:  { main: "#3b5998", dark: "#344e86" },
    twitter:   { main: "#55acee", dark: "#3ea1ec" },
    instagram: { main: "#125688", dark: "#0e456d" },
    linkedin:  { main: "#0077b5", dark: "#00669c" },
    pinterest: { main: "#cc2127", dark: "#b21d22" },
    youtube:   { main: "#e52d27", dark: "#d41f1a" },
    vimeo:     { main: "#1ab7ea", dark: "#13a3d2" },
    slack:     { main: "#3aaf85", dark: "#329874" },
    dribbble:  { main: "#ea4c89", dark: "#e73177" },
    github:    { main: "#24292e", dark: "#171a1d" },
    reddit:    { main: "#ff4500", dark: "#e03d00" },
    tumblr:    { main: "#35465c", dark: "#2a3749" },
  },

  // ─── Badge colors ─────────────────────────────────────────────────────────
  badgeColors: {
    primary: {
      background: palette.brand[100],
      text:       palette.brand[700],
    },

    secondary: {
      background: palette.neutral[200],
      text:       palette.neutral[600],
    },

    info: {
      background: palette.brand[100],
      text:       palette.brand[800],
    },

    success: {
      background: palette.green[100],
      text:       palette.green[700],
    },

    warning: {
      background: palette.amber[100],
      text:       palette.amber[800],
    },

    error: {
      background: palette.red[100],
      text:       palette.red[700],
    },

    light: {
      background: palette.white,
      text:       palette.neutral[300],
    },

    dark: {
      background: palette.navy[300],
      text:       palette.navy[900],
    },
  },

  // ─── Colored shadows ──────────────────────────────────────────────────────
  coloredShadows: {
    primary:   palette.brand[500],
    secondary: palette.neutral[900],
    info:      palette.brand[400],
    success:   palette.green[500],
    warning:   palette.amber[600],
    error:     palette.red[500],
    light:     palette.neutral[400],
    dark:      palette.navy[700],
  },

  // ─── Misc ─────────────────────────────────────────────────────────────────
  inputBorderColor: palette.neutral[300],

  tabs: {
    indicator: { boxShadow: palette.neutral[200] },
  },
};
